import React from 'react';
import { useQueryStates, parseAsString, parseAsInteger } from 'nuqs';
import { useAtom } from 'jotai';

import { notificationsHydratedAtom, notificationsListParamsAtom } from '@/store';

export interface SearchFields {
  message: string;
  type: string;
  status: string;
}

export function useNotificationsFilters() {
  const [params, setParams] = useAtom(notificationsListParamsAtom);
  const [, setHydrated] = useAtom(notificationsHydratedAtom);

  const [queryParams, setQueryParams] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    search: parseAsString.withDefault(''),
    type: parseAsString.withDefault('all'),
    status: parseAsString.withDefault('all'),
    message: parseAsString.withDefault(''),
  });

  // Filters state (client UI) mapped to API params (URL synced)
  const [typeFilter, setTypeFilter] = React.useState<string>(queryParams.type);
  const [statusFilter, setStatusFilter] = React.useState<string>(queryParams.status);
  const [searchFields, setSearchFields] = React.useState<SearchFields>({
    message: queryParams.message,
    type: queryParams.type,
    status: queryParams.status
  });

  // Search state
  const [searchTerm, setSearchTerm] = React.useState(queryParams.search);

  // Sync URL parameters with params atom on mount and when URL changes
  React.useEffect(() => {
    const filters: Record<string, string | undefined> = {};

    // Build filters from URL parameters
    if (queryParams.search) filters.search = queryParams.search;
    if (queryParams.type !== 'all') filters.type = queryParams.type;
    if (queryParams.status !== 'all') filters.status = queryParams.status;
    if (queryParams.message) filters.message = queryParams.message;

    setParams((prev) => ({
      ...prev,
      page: queryParams.page,
      filters: filters
    }));

    // Mark hydrated after first sync
    setHydrated(true);
  }, [queryParams.page, queryParams.search, queryParams.type, queryParams.status, queryParams.message, setParams]);

  // Sync URL parameters with local state on mount
  React.useEffect(() => {
    setSearchTerm(queryParams.search);
    setTypeFilter(queryParams.type);
    setStatusFilter(queryParams.status);
    setSearchFields({
      message: queryParams.message,
      type: queryParams.type,
      status: queryParams.status
    });
  }, []); // Only run on mount

  // Search handler
  const handleSearch = React.useCallback(() => {
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, search: searchTerm || undefined }
    }));
    setQueryParams({ page: 1, search: searchTerm });
  }, [searchTerm, setParams, setQueryParams]);

  // Clear search handler
  const handleClearSearch = React.useCallback(() => {
    setSearchTerm('');
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, search: undefined }
    }));
    setQueryParams({ page: 1, search: '' });
  }, [setParams, setQueryParams]);

  const applyFilters = React.useCallback(async () => {
    const nextFilters: Record<string, string | undefined> = {
      ...params.filters,
      type: typeFilter !== 'all' ? typeFilter : undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      message: searchFields.message || undefined,
    };

    setParams((prev) => ({ ...prev, page: 1, filters: nextFilters }));

    await setQueryParams({
      page: 1,
      type: typeFilter,
      status: statusFilter,
      message: searchFields.message,
    });
  }, [params.filters, typeFilter, statusFilter, searchFields, setParams, setQueryParams]);

  const clearFilters = React.useCallback(async () => {
    setSearchTerm('');
    setTypeFilter('all');
    setStatusFilter('all');
    setSearchFields({ message: '', type: 'all', status: 'all' });
    setParams((prev) => ({ ...prev, page: 1, filters: {} }));
    await setQueryParams({
      page: 1,
      search: '',
      type: 'all',
      status: 'all',
      message: '',
    });
  }, [setParams, setQueryParams]);

  const hasAnyFilter = React.useMemo(() => {
    return (
      Boolean(searchTerm) ||
      typeFilter !== 'all' ||
      statusFilter !== 'all' ||
      Boolean(searchFields.message)
    );
  }, [searchTerm, typeFilter, statusFilter, searchFields]);

  const handleTypeChange = React.useCallback((val: string) => {
    setTypeFilter(val);
    setQueryParams({ page: 1, type: val });
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, type: val !== 'all' ? val : undefined }
    }));
  }, [setQueryParams, setParams]);

  const handleStatusChange = React.useCallback((val: string) => {
    setStatusFilter(val);
    setQueryParams({ page: 1, status: val });
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, status: val !== 'all' ? val : undefined }
    }));
  }, [setQueryParams, setParams]);

  const handlePageChange = React.useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }));
    setQueryParams({ page });
  }, [setParams, setQueryParams]);

  return {
    // State
    searchTerm,
    setSearchTerm,
    typeFilter,
    statusFilter,
    searchFields,
    setSearchFields,
    hasAnyFilter,

    // Actions
    handleSearch,
    handleClearSearch,
    applyFilters,
    clearFilters,
    handleTypeChange,
    handleStatusChange,
    handlePageChange
  };
}
